import {
  Box,
  Button,
  Divider,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
} from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { colors } from 'packages/common-frontend/colors';
import { DealSubPath } from '~/backup/shared/deal-nav/useDealRouteMatch';
import {
  PortalBusDealsQueryGQLType,
  usePortalBusDealsQuery,
} from '~/backup/shared/deals.gql.generated';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  GlobalNav,
  GlobalNavRouterLinkItem,
} from '~/packages/common-frontend/layout';
import { useLayoutContext } from '~/packages/common-frontend/layout/LayoutProvider';

type Deal = PortalBusDealsQueryGQLType['portalBusDeals'][number];

/** The caller's deals, by credit-facility name for a stable dropdown order. */
function sortDeals(deals: Deal[]): Deal[] {
  return [...deals].sort((a, b) =>
    a.creditFacilityName.localeCompare(b.creditFacilityName),
  );
}

/**
 * Dropdown that lists every deal the caller is a party to and navigates to the
 * chosen one, preserving the current deal sub-page (`subPath`) so switching
 * from one deal's Settings lands on the next deal's Settings. Shares the
 * `PortalBusDeals` operation (cache-first) with the Portfolio index.
 */
function DealSwitcher({
  implementationId,
  subPath,
}: {
  implementationId: string;
  subPath: DealSubPath;
}): React.ReactElement {
  const navigate = useNavigate();
  const { data, loading, error } = usePortalBusDealsQuery({
    fetchPolicy: 'cache-first',
  });

  const deals = sortDeals(data?.portalBusDeals ?? []);
  const current = deals.find((d) => d.implementationId === implementationId);

  let label: React.ReactNode = 'Select deal';
  if (current) label = current.creditFacilityName;
  else if (loading) label = <Spinner size="xs" />;
  else if (error) label = 'Unavailable';

  return (
    <Menu matchWidth placement="bottom">
      <MenuButton
        as={Button}
        variant="outline"
        size="sm"
        width="100%"
        textAlign="left"
        fontWeight={500}
        rightIcon={<Icon icon="chevron-down" />}
        borderColor={colors.gray[200]}
      >
        <Text isTruncated>{label}</Text>
      </MenuButton>
      <MenuList maxH="20rem" overflowY="auto" zIndex="dropdown">
        {error ? (
          <Box px="0.75rem" py="0.5rem">
            <Text fontSize="0.8rem" color={colors.red[400]}>
              Could not load deals. Please try again.
            </Text>
          </Box>
        ) : (
          deals.map((d) => (
            <MenuItem
              key={d.implementationId}
              onClick={() => navigate(`/deals/${d.implementationId}${subPath}`)}
              fontWeight={d.implementationId === implementationId ? 600 : 400}
              fontSize="0.875rem"
            >
              {d.creditFacilityName}
            </MenuItem>
          ))
        )}
      </MenuList>
    </Menu>
  );
}

type Props = {
  /** The deal currently in view; scopes the Overview / Settings links. */
  implementationId: string;
  /** Sub-page suffix carried by the switcher. */
  subPath: DealSubPath;
  /** Where the "back" link points (portal index). */
  backTo: string;
  backLabel: string;
  /** Whether to surface the portfolio-level Analytics link (lender only for now). */
  showAnalytics: boolean;
  /** Whether to surface the deal Settings link (lender only). */
  showSettings: boolean;
};

/**
 * Deal-scoped sidebar shown on `/deals/:implementationId` routes (Overview, a
 * period-scoped Overview, Settings): a back-link to the portal index, a deal
 * switcher, and the deal's Overview, plus Analytics for the lender. Settings is a config
 * surface rather than a daily path, so it sits below a divider at `size="sm"` —
 * matching the secondary group in the portal sidebar. Reuses the shared
 * `GlobalNav` shell so collapse / mobile-drawer behaviour matches the flat portal
 * nav; the switcher only renders while the nav is expanded.
 */
export const BackupDealNav = ({
  implementationId,
  subPath,
  backTo,
  backLabel,
  showAnalytics,
  showSettings,
}: Props): React.ReactElement => {
  const layout = useLayoutContext();
  return (
    <GlobalNav isFixed offsetHeaderCount={1}>
      <GlobalNavRouterLinkItem
        to={backTo}
        label={backLabel}
        icon={<Icon icon="arrow-left" />}
      />
      {layout.globalNavOpen && (
        <Box px="0.75rem" py="0.5rem">
          <DealSwitcher implementationId={implementationId} subPath={subPath} />
        </Box>
      )}
      {/* `end` so nested deal routes don't light this up — except a period-scoped
          Overview, which *is* this page and arrives with an empty subPath. */}
      <GlobalNavRouterLinkItem
        to={`/deals/${implementationId}`}
        end
        isActive={subPath === ''}
        label="Overview"
        icon={<Icon icon="house" />}
      />
      {showAnalytics && (
        <GlobalNavRouterLinkItem
          to="/analytics"
          label="Analytics"
          icon={<Icon icon="chart-mixed" />}
        />
      )}
      {showSettings && (
        <>
          <Divider margin="0.5rem 0" width="auto" />
          <GlobalNavRouterLinkItem
            to={`/deals/${implementationId}/settings`}
            label="Settings"
            icon={<Icon icon="gear" />}
            size="sm"
          />
        </>
      )}
    </GlobalNav>
  );
};
